import type {
  PlaybookCriterionNode,
  PlaybookNode,
  PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type {
  FeedbackWithCurrentGrade,
  TestGradingResult,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  SlideoverDialog,
  SlideoverDialogButtons,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { IconFlask } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useTestNodeGradingMutation } from "../../api/entities/playbook.js";
import { CriteriaForm } from "./criteriaForm.js";
import {
  calculateFeedbackAccuracyWithCurrentGrades,
  calculateSectionFeedbackAccuracyWithCurrentGrades,
  calculateTestAccuracyWithResults,
} from "./feedbackAccuracyHelpers.js";
import { FeedbackAccuracySummary } from "./feedbackAccuracySummary.js";
import { GraderFeedbackTable } from "./graderFeedback/graderFeedbackTable.js";
import { SectionForm } from "./sectionForm.js";

interface GraderFeedbackSlideoverDialogProps {
  feedbacks: readonly FeedbackWithCurrentGrade[] | undefined;
  onFeedbackClick: (feedback: FeedbackWithCurrentGrade) => void;
  node: PlaybookNode;
  onUpdateCriterion?: (criteria: Partial<PlaybookCriterionNode>) => void;
  onUpdateSection?: (section: Partial<PlaybookSectionNode>) => void;
}

export const GraderFeedbackSlideoverDialog: FC<
  GraderFeedbackSlideoverDialogProps
> = ({
  feedbacks = [],
  onFeedbackClick,
  node,
  onUpdateCriterion,
  onUpdateSection,
}) => {
  const [testNodeGrading] = useTestNodeGradingMutation();

  const [testNode, setTestNode] = useState<PlaybookNode>(node);
  const [isTestingGrading, setIsTestingGrading] = useState(false);
  const [testResults, setTestResults] = useState<TestGradingResult[] | null>(
    null,
  );
  const [testError, setTestError] = useState<string | null>(null);
  const [hasEdits, setHasEdits] = useState(false);

  const isSection = node.type === "section";

  const onClickTestGrading = async () => {
    setIsTestingGrading(true);
    setTestError(null);

    try {
      const feedbackIds = feedbacks.map((f) => f.id);

      // Assuming we have at least one feedback and all feedbacks are attached to the same playbook.
      const playbookId = feedbacks.at(0)?.recording_assessment_template_id;

      if (!playbookId) {
        throw new Error("Test grading requires at least one feedback");
      }

      const results = await testNodeGrading({
        playbookId,
        feedbackIds,
        node: testNode,
      }).unwrap();

      setTestResults(results);
    } catch (error) {
      setTestError(
        error instanceof Error ? error.message : "Failed to test grading",
      );
    } finally {
      setIsTestingGrading(false);
    }
  };

  const currentAccuracy =
    feedbacks.length > 0
      ? isSection
        ? calculateSectionFeedbackAccuracyWithCurrentGrades(node.id, feedbacks)
        : calculateFeedbackAccuracyWithCurrentGrades(node.id, feedbacks)
      : null;

  const testAccuracy = testResults
    ? calculateTestAccuracyWithResults(feedbacks, testResults)
    : null;

  const renderButtons = ({ close }: { close: () => void }) => (
    <>
      <Button
        variant={ButtonVariant.Secondary}
        size={Size.MEDIUM}
        onClick={close}
        isDisabled={!hasEdits && !testResults}
      >
        Discard
      </Button>
      <Button
        variant={ButtonVariant.Primary}
        size={Size.MEDIUM}
        onClick={() => {
          if (hasEdits) {
            if (testNode.type === "section" && onUpdateSection) {
              onUpdateSection(testNode);
            } else if (testNode.type === "criteria" && onUpdateCriterion) {
              onUpdateCriterion(testNode);
            }
          }

          close();
        }}
      >
        Done
      </Button>
    </>
  );

  return (
    <SlideoverDialog label="Test grading changes">
      {({ close }) => (
        <>
          <SlideoverDialogTitleHeader isLoading={false}>
            Test grading changes
          </SlideoverDialogTitleHeader>

          <SlideoverDialogContent>
            <div className="flex h-full">
              {/* Left Panel - Criteria/Section Configuration */}
              <div className="flex w-1/3 flex-col border-r border-border-light">
                <div className="relative overflow-y-auto p-4">
                  {testNode.type === "section" ? (
                    <SectionForm
                      section={testNode}
                      onUpdateSection={(updates) => {
                        setTestNode({
                          ...testNode,
                          ...updates,
                        });
                        setHasEdits(true);
                      }}
                      isReadOnly={false}
                    />
                  ) : (
                    <CriteriaForm
                      criteria={testNode}
                      onUpdateCriterion={(updates) => {
                        setTestNode({
                          ...testNode,
                          ...updates,
                        });
                        setHasEdits(true);
                      }}
                      isReadOnly={false}
                    />
                  )}
                </div>
              </div>

              {/* Right Panel - Results Table */}
              <div className="flex flex-1 flex-col">
                <div className="relative overflow-y-auto p-4">
                  {isTestingGrading ? (
                    <div className="flex h-full items-center justify-center">
                      <Loader style={LoaderStyle.ORBIT} />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">
                          Grading performance {testResults ? "comparison" : ""}
                        </h3>
                        {currentAccuracy && (
                          <div className="mt-3 space-y-3">
                            <div className="rounded-lg bg-gray-50 p-3">
                              <h4 className="mb-2 text-sm font-medium text-foreground">
                                Current results
                              </h4>
                              <FeedbackAccuracySummary
                                accuracyData={currentAccuracy}
                              />
                            </div>

                            {testAccuracy && (
                              <div className="rounded-lg bg-blue-50 p-3">
                                <h4 className="mb-2 text-sm font-medium text-foreground">
                                  Test results
                                </h4>
                                <div className="flex items-center justify-between">
                                  <FeedbackAccuracySummary
                                    accuracyData={testAccuracy}
                                  />
                                  {testAccuracy.accuracy !==
                                    currentAccuracy.accuracy && (
                                    <span
                                      className={twMerge(
                                        "rounded-full px-2 py-1 text-sm font-medium",
                                        testAccuracy.accuracy >
                                          currentAccuracy.accuracy
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800",
                                      )}
                                    >
                                      {testAccuracy.accuracy >
                                      currentAccuracy.accuracy
                                        ? "+"
                                        : ""}
                                      {(
                                        testAccuracy.accuracy -
                                        currentAccuracy.accuracy
                                      ).toFixed(0)}
                                      %{" "}
                                      {testAccuracy.accuracy >
                                      currentAccuracy.accuracy
                                        ? "improvement"
                                        : "decline"}
                                    </span>
                                  )}
                                  {testAccuracy.accuracy ===
                                    currentAccuracy.accuracy && (
                                    <span className="rounded-full bg-gray-100 px-2 py-1 text-sm font-medium text-gray-600">
                                      No change
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="mt-2">
                              <Button
                                variant={ButtonVariant.Primary}
                                size={Size.MEDIUM}
                                icon={<IconFlask className="size-4" />}
                                onClick={onClickTestGrading}
                                isDisabled={
                                  isTestingGrading || !testNode.name.trim()
                                }
                              >
                                Test grading
                              </Button>
                              <div>
                                <p className="mt-2 text-sm text-muted-foreground">
                                  Test changes to the grading instructions to
                                  get an accuracy comparison (using up to the 20
                                  most recent grades).
                                </p>
                              </div>

                              {testError && (
                                <p className="mt-2 text-sm text-destructive">
                                  {testError}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <GraderFeedbackTable
                        feedbacks={feedbacks}
                        testGradingResults={testResults}
                        onFeedbackClick={onFeedbackClick}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </SlideoverDialogContent>

          <SlideoverDialogButtons>
            {renderButtons({ close })}
          </SlideoverDialogButtons>
        </>
      )}
    </SlideoverDialog>
  );
};
